import nanoid from 'nanoid';
import Model from '@newspaces/zero-base/Model';
import Organisation from './Organisation.js';
import { A as AccessSite } from '../AccessSite-5abc043f.js';
import DateTime from '@newspaces/zero-base/DateTime';
import AccessTimeSchedule from './AccessTimeSchedule.js';
import 'nanoid/generate';
import '@newspaces/zero-base/Runtime';
import '@newspaces/zero-base/models/Organisation';
import '@newspaces/zero-base/utils';
import '../AccessCountries-3e45c6a9.js';
import '../i18n.js';
import '@newspaces/zero-base/i18n';

class AccessConfigurationSettingsBase extends Model {
  static Fields = {};
  constructor(data) {
    super(AccessConfigurationSettingsBase.Fields, data);
  }
}

class AccessConfigurationSettingsBleSchedule extends Model {
  static Fields = {
    schedule: {
      type: AccessTimeSchedule,
      required: true,
      description: "The actual time schedule"
    },
    isPowerAdvertisingSchedule: {
      type: Boolean,
      required: true,
      default: false,
      description: "Indicates this schedule is used for max power sending"
    }
  };
  constructor(data) {
    super(AccessConfigurationSettingsBleSchedule.Fields, data);
  }
}
class AccessConfigurationSettingsBle extends Model {
  static Fields = {
    isAdvertising: {
      type: Boolean,
      default: true,
      required: true,
      description: "Sets ble enabled or not on the device. If disabled ble becomes only active via schedules"
    },
    advertisingSchedules: {
      type: [AccessConfigurationSettingsBleSchedule],
      default: () => [
        new AccessConfigurationSettingsBleSchedule({
          schedule: {
            monthStart: 1,
            monthEnd: 12,
            dateStart: 1,
            dateEnd: 31,
            weekdays: [true, true, true, true, true, true, true],
            timeStart: new DateTime.Time({ hours: 0, minutes: 0 }),
            timeEnd: new DateTime.Time({ hours: 23, minutes: 59 })
          },
          isPowerAdvertisingSchedule: true
        })
      ],
      description: "Optional schedules when ble becomes active or to define max power ble schedules"
    },
    advertisingIntervalMs: {
      type: Number,
      required: true,
      default: 500,
      min: 100,
      max: 3e3,
      description: "Defines the advertising interval in milliseconds"
    },
    maxConnectionTimeoutMs: {
      type: Number,
      required: true,
      default: 3e3,
      min: 1e3,
      max: 1e4,
      description: "Defines the maximal connection timeout in milliseconds"
    },
    txPowerLevel: {
      type: Number,
      required: true,
      default: 3,
      min: 1,
      max: 7,
      description: "The power sending level with 1 being lowest and 7 being the highest"
    },
    advertisedTxPower1Meter: {
      type: Number,
      required: true,
      default: -77,
      min: -127,
      max: 127,
      description: "The actual tx power at 1 meter distance in decibels"
    },
    isIBeaconAdvertisement: {
      type: Boolean,
      required: true,
      default: true,
      description: "If set, sends as iBeacon and thus supports background location"
    }
  };
  constructor(data) {
    super(AccessConfigurationSettingsBle.Fields, data);
  }
}

class AccessConfigurationSettingsOnline extends Model {
  static Fields = {
    connectSchedules: {
      type: [AccessTimeSchedule],
      default: () => [
        new AccessTimeSchedule({
          monthStart: 1,
          monthEnd: 12,
          dateStart: 1,
          dateEnd: 31,
          weekdays: [true, true, true, true, true, true, true],
          timeStart: new DateTime.Time({ hours: 23, minutes: 59 }),
          timeEnd: new DateTime.Time({ hours: 23, minutes: 59 })
        })
      ],
      validate: ({ connectSchedules }) => {
        if (connectSchedules.some((schedule) => {
          if (!schedule.timeStart || !schedule.timeEnd) {
            return false;
          }
          const timeStart = new DateTime.Time(schedule.timeStart, { forceUTC: true });
          const timeEnd = new DateTime.Time(schedule.timeEnd, { forceUTC: true });
          const startMins = timeStart.hours * 60 + timeStart.minutes;
          const endMins = timeEnd.hours * 60 + timeEnd.minutes;
          return startMins !== endMins;
        })) {
          return "noEqualTime";
        }
        return null;
      },
      description: "Optional schedules when online connection should be made to synch with backend"
    },
    wifiSSID: {
      type: String,
      min: 0,
      max: 32,
      description: "The SSID of the wifi to be used"
    },
    wifiPassword: {
      type: String,
      min: 0,
      max: 32,
      description: "The password of the wifi to be used"
    },
    timeoutSec: {
      type: Number,
      required: true,
      default: 30,
      min: 1,
      max: 30,
      description: "Timeout for online connections before breaking up"
    }
  };
  constructor(data) {
    super(AccessConfigurationSettingsOnline.Fields, data);
  }
}

class AccessConfigurationSettingsOssSo extends Model {
  static Fields = {
    updater: {
      type: Boolean,
      required: true,
      default: false,
      description: "If set will update offline cards with refreshed data if online"
    },
    pushEvents: {
      type: Boolean,
      required: true,
      default: false,
      description: "If set will transfer any events from offline cards to backend if online"
    },
    writePendingEvents: {
      type: Boolean,
      required: true,
      default: true,
      description: "If set will write outstanding events to an offline card"
    },
    updateFromBlacklist: {
      type: Boolean,
      required: true,
      default: true,
      description: "If set will update its blacklist from an offline card"
    }
  };
  constructor(data) {
    super(AccessConfigurationSettingsOssSo.Fields, data);
  }
}

class AccessConfigurationSettingsLock extends Model {
  static Fields = {
    openSchedules: {
      type: [AccessTimeSchedule],
      validate: ({ openSchedules }) => {
        if (openSchedules?.length > 4) {
          return "maxFourSchedules";
        }
        return null;
      },
      description: "Optional schedules when the lock should auto-open and close"
    },
    openScheduleByUser: {
      type: Boolean,
      default: false,
      description: "If set then any auto-open only occurs after first user interaction"
    },
    keepClosedOnHolidays: {
      type: Boolean,
      default: true,
      description: "If set will not auto-open on federal holidays at the given location"
    },
    defaultOpenTimeSec: {
      type: Number,
      required: true,
      default: 5,
      min: 1,
      max: 120,
      description: "Time to keep lock open in seconds after user opened it in default mode"
    },
    extendedOpenTimeSec: {
      type: Number,
      required: true,
      default: 180,
      min: 1,
      max: 86400,
      description: "Time to keep lock open in seconds after user opened it in extended mode",
      validate: ({ defaultOpenTimeSec, extendedOpenTimeSec }) => {
        if (extendedOpenTimeSec < defaultOpenTimeSec) {
          return "extendedLowerThanDefaultOpenTimeSec";
        }
        return null;
      }
    }
  };
  constructor(data) {
    super(AccessConfigurationSettingsLock.Fields, data);
  }
}

class AccessConfigurationSettings extends Model {
  static Fields = {
    base: {
      type: AccessConfigurationSettingsBase
    },
    ble: {
      type: AccessConfigurationSettingsBle
    },
    online: {
      type: AccessConfigurationSettingsOnline
    },
    ossSo: {
      type: AccessConfigurationSettingsOssSo
    },
    lock: {
      type: AccessConfigurationSettingsLock
    }
  };
  constructor(data) {
    super(AccessConfigurationSettings.Fields, data);
  }
}

class AccessConfiguration extends Model {
  static CollectionName = "accessConfigurations";
  static Fields = {
    organisation: {
      type: Organisation,
      serialize: "insert",
      required: true
    },
    sites: {
      type: [AccessSite],
      description: "A list of sites this configuration is limited too or empty to be available everywhere"
    },
    attached: {
      type: Boolean,
      serialize: "insert",
      default: false,
      description: "If set then this configuration is bound to a single object and not shared"
    },
    name: {
      type: String,
      required: ({ attached }) => !attached,
      description: "A proper name describing the configuration"
    },
    description: {
      type: String,
      description: "Optional detailed description for the configuration"
    },
    configId: {
      type: String,
      max: 8,
      min: 8,
      required: true,
      unique: "global",
      default: () => nanoid(8),
      description: "The current id for this configuration which changes every time it is modified"
    },
    settings: {
      type: AccessConfigurationSettings,
      description: "The actual settings for the configuration"
    }
  };
  constructor(data) {
    super(AccessConfiguration.Fields, data);
  }
}
Model.declareModel(AccessConfiguration);

AccessConfiguration.AccessConfigurationSettingsBleSchedule = AccessConfigurationSettingsBleSchedule;

export { AccessConfiguration as default };
//# sourceMappingURL=AccessConfiguration.js.map
